import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Leaf, Truck, Clock } from "lucide-react";
import { MaskLine, Reveal, fadeUp, stagger, ease } from "@/lib/motion";
import { IMAGES } from "@/lib/media";
import BrandMarquee from "@/components/BrandMarquee";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden pt-28 pb-14">
      <div className="mx-auto max-w-[1400px] w-full px-4 sm:px-8 relative z-10">
        <motion.div initial="hidden" animate="show" variants={stagger} className="flex items-center gap-4 mb-8">
          <motion.span variants={fadeUp} className="h-px w-14 bg-[#C85A32]" />
          <motion.span variants={fadeUp} className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#4A5A4E]">
            Distribuzione ortofrutticola · Italia
          </motion.span>
        </motion.div>

        <h1 className="font-serif-display text-[#1A3322] tracking-tighter leading-[0.9] text-6xl sm:text-8xl lg:text-[9.5rem]">
          <MaskLine delay={0.15}>La freschezza</MaskLine>
          <MaskLine delay={0.3} className="text-[#C85A32] italic font-light">che si sente</MaskLine>
          <MaskLine delay={0.45}>in ogni piatto.</MaskLine>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease, delay: 0.9 }}
          className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end"
        >
          <p className="lg:col-span-6 text-lg leading-relaxed text-[#4A5A4E] max-w-xl">
            <span className="text-[#1A3322] font-semibold">B FRUIT SRL</span> seleziona ogni giorno frutta
            e verdura per ristoranti, alberghi, hotel e supermercati. Piccola azienda, grande cura.
          </p>
          <div className="lg:col-span-6 flex flex-wrap gap-4 lg:justify-end">
            <Link to="/servizi" data-testid="hero-cta-servizi" className="group inline-flex items-center gap-2 rounded-full bg-[#1A3322] text-[#F4F2EE] px-7 py-4 text-sm uppercase tracking-widest hover:bg-[#C85A32] transition-colors duration-300">
              I nostri servizi
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/contatti" data-testid="hero-cta-contatti" className="inline-flex items-center gap-2 rounded-full border border-[#1A3322]/25 px-7 py-4 text-sm uppercase tracking-widest text-[#1A3322] hover:bg-[#1A3322] hover:text-[#F4F2EE] transition-colors duration-300">
              Contattaci
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Floating clipped produce image */}
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease, delay: 0.6 }}
        className="pointer-events-none absolute right-4 sm:right-8 top-24 lg:top-20 w-[38vw] max-w-[420px] hidden md:block"
      >
        <div className="arch overflow-hidden shadow-2xl">
          <motion.img style={{ y, scale }} src={IMAGES.heroVeg} alt="Frutta e verdura fresca" className="w-full h-[62vh] object-cover" />
        </div>
      </motion.div>
    </section>
  );
}

const feats = [
  { icon: Leaf, t: "Selezione premium", d: "Prodotti scelti a mano all'alba, direttamente dal mercato ortofrutticolo." },
  { icon: Truck, t: "Consegne dedicate", d: "Mezzi refrigerati e logistica su misura per la tua attività." },
  { icon: Clock, t: "Consegna in 24 ore", d: "Consegniamo entro 24 ore dall'ordine: dal banco alla tua cucina, sempre puntuali." },
];

function Manifesto() {
  const chapters = [
    { n: "01", t: "Selezioniamo", d: "Ogni mattina scegliamo solo il meglio della stagione, controllando maturazione, colore e provenienza." },
    { n: "02", t: "Prepariamo", d: "Confezioniamo su misura per ristoranti, hotel e supermercati, rispettando tempi e standard di qualità." },
    { n: "03", t: "Consegniamo", d: "Portiamo la freschezza fino alla tua porta con mezzi refrigerati e consegne puntuali." },
  ];
  return (
    <section data-testid="manifesto" className="mx-auto max-w-[1400px] px-4 sm:px-8 py-24 lg:py-36">
      <Reveal>
        <p className="text-xs uppercase tracking-[0.3em] text-[#C85A32] mb-4">Il nostro metodo</p>
        <h2 className="font-serif-display text-5xl sm:text-7xl tracking-tight text-[#1A3322] max-w-3xl">
          Tre gesti semplici, <span className="italic font-light">fatti bene.</span>
        </h2>
      </Reveal>
      <div className="mt-16 divide-y divide-[#1A3322]/10 border-t border-[#1A3322]/10">
        {chapters.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.08}>
            <div className="group grid grid-cols-1 md:grid-cols-12 gap-6 py-10 items-baseline transition-colors duration-500 hover:bg-[#EAE7E1]/60 -mx-4 px-4 rounded-2xl">
              <span className="md:col-span-3 font-serif-display text-7xl sm:text-8xl text-[#8A9A5B] leading-none">{c.n}</span>
              <h3 className="md:col-span-4 font-serif-display text-4xl text-[#1A3322]">{c.t}</h3>
              <p className="md:col-span-5 text-[#4A5A4E] leading-relaxed text-lg">{c.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ChiSiamoTeaser() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 60]);
  return (
    <section ref={ref} className="bg-[#EAE7E1]">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C85A32] mb-5">Chi siamo</p>
            <h2 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl tracking-tight text-[#1A3322] leading-[0.95]">
              Una piccola azienda <span className="italic font-light text-[#C85A32]">di famiglia</span>, un servizio da grandi.
            </h2>
            <p className="mt-8 text-lg leading-relaxed text-[#4A5A4E] max-w-xl">
              Nasciamo dalla passione per il prodotto fresco e dal rapporto diretto con chi cucina.
              Conosciamo i nostri clienti per nome e trattiamo ogni consegna come se fosse per la nostra tavola.
            </p>
            <Link to="/chi-siamo" data-testid="teaser-chi-siamo" className="group mt-9 inline-flex items-center gap-2 text-[#1A3322] text-lg font-medium link-underline">
              Scopri la nostra storia
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative">
            <div className="arch overflow-hidden shadow-xl">
              <motion.img style={{ y }} src={IMAGES.crates} alt="Casse di verdura fresca" className="w-full h-[60vh] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#1A3322] text-[#F4F2EE] rounded-2xl px-7 py-5 shadow-2xl">
              <p className="font-serif-display text-4xl">24h</p>
              <p className="text-xs uppercase tracking-widest text-[#F4F2EE]/70">consegna dall'ordine</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiziTeaser() {
  const cards = [
    { t: "Ristoranti", d: "Materie prime d'eccellenza per chef e brigate di cucina.", img: IMAGES.chef, span: "lg:col-span-7" },
    { t: "Hotel & Alberghi", d: "Forniture continue per colazioni, buffet e banqueting.", img: IMAGES.fruitPlate, span: "lg:col-span-5" },
    { t: "Supermercati", d: "Reparto ortofrutta sempre rifornito e curato.", img: IMAGES.marketVeg, span: "lg:col-span-5" },
    { t: "Logistica dedicata", d: "Mezzi refrigerati e consegne programmate su misura.", img: IMAGES.van, span: "lg:col-span-7" },
  ];
  return (
    <section data-testid="servizi-teaser" className="mx-auto max-w-[1400px] px-4 sm:px-8 py-24 lg:py-36">
      <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C85A32] mb-4">Cosa facciamo</p>
          <h2 className="font-serif-display text-5xl sm:text-7xl tracking-tight text-[#1A3322]">Serviamo chi<br/><span className="italic font-light">serve bene.</span></h2>
        </div>
        <Link to="/servizi" className="group inline-flex items-center gap-2 text-[#1A3322] link-underline text-lg">
          Tutti i servizi <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {cards.map((c, i) => (
          <Reveal key={c.t} delay={i * 0.06} className={c.span}>
            <div className="group relative h-[46vh] min-h-[340px] overflow-hidden rounded-3xl border border-[#1A3322]/10">
              <img src={c.img} alt={c.t} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A3322]/85 via-[#1A3322]/20 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <h3 className="font-serif-display text-4xl text-[#F4F2EE]">{c.t}</h3>
                <p className="mt-2 text-[#F4F2EE]/80 max-w-sm">{c.d}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FriendsQuote() {
  return (
    <section data-testid="friends-quote" className="bg-[#1A3322] text-[#F4F2EE]">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-8 py-24 lg:py-36">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#E8801F] mb-8">La nostra promessa</p>
          <blockquote className="font-serif-display text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-5xl">
            Non solo fornitori: <span className="italic font-light text-[#E8801F]">vogliamo esserti vicini, come un amico.</span>
          </blockquote>
          <p className="mt-8 text-lg text-[#F4F2EE]/70 max-w-xl">
            Costruiamo con ogni cliente un rapporto diretto e sincero, fatto di fiducia e attenzione, giorno dopo giorno.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="mx-auto max-w-[1400px] px-4 sm:px-8 pb-24 lg:pb-36">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2.5rem] bg-[#1A3322] px-8 sm:px-16 py-20 text-center">
          <img src={IMAGES.fruitPile} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
          <div className="relative z-10">
            <h2 className="font-serif-display text-5xl sm:text-7xl text-[#F4F2EE] tracking-tight">
              Pronti a rifornire <span className="italic font-light text-[#E8801F]">la tua cucina?</span>
            </h2>
            <p className="mt-6 text-[#F4F2EE]/75 text-lg max-w-xl mx-auto">
              Contattaci per informazioni e per effettuare un ordine: prepariamo una
              fornitura su misura, fresca e consegnata entro 24 ore.
            </p>
            <Link to="/contatti" data-testid="cta-contatti" className="group mt-10 inline-flex items-center gap-2 rounded-full bg-[#F4F2EE] text-[#1A3322] px-8 py-4 text-sm uppercase tracking-widest hover:bg-[#E8801F] hover:text-[#F4F2EE] transition-colors duration-300">
              Richiedi un preventivo
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <BrandMarquee />
      <section className="mx-auto max-w-[1400px] px-4 sm:px-8 py-24 lg:py-32 grid grid-cols-1 md:grid-cols-3 gap-10">
        {feats.map((f, i) => (
          <Reveal key={f.t} delay={i * 0.1}>
            <div className="group">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1A3322] text-[#F4F2EE] transition-colors duration-300 group-hover:bg-[#C85A32]">
                <f.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-6 font-serif-display text-3xl text-[#1A3322]">{f.t}</h3>
              <p className="mt-3 text-[#4A5A4E] leading-relaxed">{f.d}</p>
            </div>
          </Reveal>
        ))}
      </section>
      <ChiSiamoTeaser />
      <FriendsQuote />
      <Manifesto />
      <ServiziTeaser />
      <CTA />
    </main>
  );
}
